import React from "react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { ArrowRight, DollarSign, PlugZap, ShieldAlert, Timer, UserX } from "lucide-react";
import { FOCUS_RING } from "./socUi";
import { freshnessOf } from "./socFreshness";
import {
  postureOf, dailyIncidents, formatDuration, meanTimeToAssign, periodChange, previousIncidents, urgentOwnership,
} from "./socConsole";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });

// One colour-filled KPI card. With onClick the card is a button and shows its action; without it the
// card is information only. Text is white on a theme gradient (--soc-kpi-N), which reads in both themes.
const Kpi = ({ gradient, label, icon: Icon, testId, onClick, action, title, children }) => {
  const body = (
    <div data-testid={testId} className="relative z-10 flex h-full flex-col gap-1">
      <div className="flex items-center justify-between gap-2 text-[13px] font-semibold text-white/90">
        <span>{label}</span>
        {Icon && <Icon className="h-4 w-4 text-white/85" aria-hidden="true" />}
      </div>
      {children}
      {onClick && action && (
        <span className="mt-auto flex items-center gap-1 pt-1 text-xs font-semibold text-white/85 group-hover:text-white">
          {action} <ArrowRight className="h-3 w-3 transition-transform motion-safe:group-hover:translate-x-0.5" aria-hidden="true" />
        </span>
      )}
    </div>
  );
  const className =
    "group relative flex min-h-[9.5rem] w-full flex-col overflow-hidden rounded-xl p-4 text-left text-white " +
    "shadow-[0_12px_24px_-14px_rgba(15,23,42,0.6)] transition-transform duration-200 " +
    "after:pointer-events-none after:absolute after:-right-10 after:-top-10 after:h-36 after:w-36 after:rounded-full after:bg-white/10 " +
    (onClick ? `motion-safe:hover:-translate-y-0.5 ${FOCUS_RING}` : "");
  return onClick ? (
    <button type="button" onClick={onClick} className={className} style={{ background: gradient }} title={title}>{body}</button>
  ) : (
    <div className={className} style={{ background: gradient }} title={title}>{body}</div>
  );
};

const Big = ({ children, small }) => (
  <p className="text-[2rem] font-extrabold leading-none tracking-tight tabular-nums">
    {children}
    {small && <>{" "}<span className="text-base font-semibold text-white/80">{small}</span></>}
  </p>
);

const Sub = ({ children }) => <p className="text-xs font-medium text-white/85">{children}</p>;

// White area sparkline for a coloured card.
const WhiteSpark = ({ values, id }) => (
  <div className="mt-auto h-11" aria-hidden="true">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={values.map((v, i) => ({ i, v }))} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity={0.45} />
            <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke="#ffffff" strokeWidth={2} fill={`url(#${id})`} dot={false} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

// A white ring showing a share (unassigned of urgent).
const WhiteRing = ({ part, whole }) => {
  const r = 17;
  const c = 2 * Math.PI * r;
  const share = whole > 0 ? Math.min(1, part / whole) : 0;
  return (
    <svg width="46" height="46" viewBox="0 0 46 46" aria-hidden="true" className="shrink-0">
      <circle cx="23" cy="23" r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="6" />
      <circle cx="23" cy="23" r={r} fill="none" stroke="#ffffff" strokeWidth="6" strokeLinecap="round"
        strokeDasharray={`${(share * c).toFixed(1)} ${c.toFixed(1)}`} transform="rotate(-90 23 23)" />
    </svg>
  );
};

const SQUARE = {
  healthy: "bg-white",
  review: "bg-white",
  action: "bg-white",
  stale: "bg-amber-300",
  notConnected: "bg-white/25",
};

// The Overview's KPI band: five colour-filled cards. Every figure comes from data the page already has.
const SocKpiBand = ({ tiles, incidents, trends, queue, exposure, now, onIncidents, onUnassigned, onConnectors }) => {
  const incidentsConnected = !!incidents && incidents.status !== "notConnected" && incidents.connected !== false;
  const m = incidents?.metrics || {};
  const openIncidents = incidentsConnected ? (Number(m.openIncidents) || 0) + (Number(m.openAlerts) || 0) : null;
  const series = dailyIncidents(trends);
  // New incidents + alerts this period vs the previous one (the open count has no history).
  const change = incidentsConnected && trends ? periodChange(series.reduce((a, b) => a + b, 0), previousIncidents(trends)) : null;
  const days = trends?.days;
  // One severity per line, full word and count, so the split reads at a glance.
  const severityRows = incidentsConnected
    ? [["critical", "Critical"], ["high", "High"], ["medium", "Medium"], ["low", "Low"]]
        .filter(([k]) => Object.prototype.hasOwnProperty.call(m, k))
        .map(([k, label]) => [k, label, Number(m[k]) || 0])
    : [];

  const queueReady = !queue.error && !(queue.loading && queue.rows.length === 0);
  const own = urgentOwnership(queue.rows);

  const postures = (tiles || []).map((t) => ({ key: t.key, posture: postureOf(t, now) }));
  // Counted as the Data connectors panel counts them: an area is receiving data only when its last
  // data is recent, so a stale area or one with no sync time is connected but not receiving.
  const freshness = (tiles || []).map((t) => freshnessOf(t, now).state);
  const connected = freshness.filter((f) => f !== "notConnected").length;
  const receiving = freshness.filter((f) => f === "fresh").length;
  const stale = freshness.filter((f) => f === "stale").length;
  const unknown = freshness.filter((f) => f === "unknown").length;

  const mtta = queueReady ? meanTimeToAssign(queue.rows) : null;
  const mttaText = mtta ? formatDuration(mtta.ms) : null;

  const withEstimate = Number(exposure?.risksWithEstimate) || 0;
  const without = Number(exposure?.risksWithoutEstimate) || 0;

  return (
    <section aria-label="Key metrics" data-testid="soc-kpi-row" className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
      <Kpi
        gradient="var(--soc-kpi-1)"
        label="Open incidents & alerts"
        icon={ShieldAlert}
        testId="soc-kpi-incidents"
        onClick={incidentsConnected ? onIncidents : undefined}
        action="View in queue"
      >
        <Big>{openIncidents ?? "—"}</Big>
        {severityRows.length > 0 && (
          <dl className="grid w-max grid-cols-[auto_auto] gap-x-3 text-xs font-medium text-white/85" data-testid="soc-kpi-incidents-severity">
            {severityRows.map(([k, label, value]) => (
              <React.Fragment key={k}>
                <dt>{label}</dt>
                <dd className="text-right font-semibold tabular-nums text-white" data-testid={`soc-kpi-incidents-${k}`}>{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
        {change && (
          <p className="text-xs font-semibold text-white" data-testid="soc-kpi-incidents-change"
            title={`New incidents and alerts in the last ${days} days compared with the ${days} days before. A source connected during that earlier period makes it look quieter.`}>
            <span className="mr-1 rounded bg-white/20 px-1.5 py-px">
              {change.delta === 0
                ? "No change"
                : `${change.delta > 0 ? "+" : "−"}${Math.abs(change.delta)}${change.pct != null ? ` (${change.delta > 0 ? "▲" : "▼"} ${Math.abs(change.pct)}%)` : ""}`}
            </span>
            {change.previousNone ? `new · none in the previous ${days} days` : `new vs previous ${days} days`}
          </p>
        )}
        {incidentsConnected && series.length > 1 && <WhiteSpark values={series} id="soc-kpi-spark-incidents" />}
      </Kpi>

      <Kpi
        gradient="var(--soc-kpi-2)"
        label="Unassigned critical & high"
        icon={UserX}
        testId="soc-kpi-unassigned"
        onClick={queueReady ? onUnassigned : undefined}
        action="Show unassigned"
      >
        {queue.error ? (
          <>
            <Big>—</Big>
            <Sub>The incident queue couldn't be loaded</Sub>
          </>
        ) : !queueReady ? (
          <Big>…</Big>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <Big small={`of ${own.urgent}`}>{own.unassigned}</Big>
              <WhiteRing part={own.unassigned} whole={own.urgent} />
            </div>
            <Sub>Open queue items with no owner</Sub>
          </>
        )}
      </Kpi>

      <Kpi
        gradient="var(--soc-kpi-3)"
        label="Financial exposure"
        icon={DollarSign}
        testId="soc-exposure"
        title="Annualized exposure of the open Defender XDR, attack surface, secrets and leaked-credential risks"
      >
        <Big>{currency.format(Number(exposure?.total) || 0)}</Big>
        <Sub>Annualized (FAIR), open risks</Sub>
        {withEstimate + without > 0 && (
          <div className="mt-auto space-y-1">
            <div className="flex h-1.5 overflow-hidden rounded-full bg-white/25" aria-hidden="true">
              <span className="block bg-white" style={{ width: `${(withEstimate / (withEstimate + without)) * 100}%` }} />
            </div>
            <Sub>{without > 0 ? `${without} findings without an estimate` : "Every open finding has an estimate"}</Sub>
          </div>
        )}
      </Kpi>

      <Kpi
        gradient="var(--soc-kpi-4)"
        label="Areas receiving data"
        icon={PlugZap}
        testId="soc-kpi-connected"
        onClick={onConnectors}
        action="See data connectors"
        title="Connected areas whose data is recent, out of all connected areas"
      >
        <Big small={`of ${connected} connected`}>{receiving}</Big>
        <div className="flex flex-wrap gap-1" aria-hidden="true">
          {postures.map((p) => (
            <span key={p.key} title={p.key} className={`h-3 w-3 rounded-[3px] ${SQUARE[p.posture]}`} />
          ))}
        </div>
        <Sub>{stale + unknown > 0
          ? [stale > 0 && `${stale} stale`, unknown > 0 && `${unknown} with no sync time`].filter(Boolean).join(" · ")
          : connected > 0 ? "All connected areas current" : "No areas connected"}</Sub>
      </Kpi>

      <Kpi
        gradient="var(--soc-kpi-5)"
        label="Mean time to assign"
        icon={Timer}
        testId="soc-kpi-mtta"
        title="From creation to the latest assignment, over open items that record both"
      >
        {mttaText ? (
          <>
            <Big small={mttaText.unit}>{mttaText.value}</Big>
            <Sub>{`Open items · ${mtta.count} assigned`}</Sub>
          </>
        ) : (
          <>
            <Big>—</Big>
            <Sub>{queueReady ? "No open item has an assignment time yet" : "Waiting for the incident queue"}</Sub>
          </>
        )}
      </Kpi>
    </section>
  );
};

export default SocKpiBand;
